import { useEffect, useMemo, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { supabase } from "../supabaseClient";
import SendReminderButton from "../components/SendReminderButton";
import {
  Dumbbell,
  CalendarDays,
  MapPin,
  FileText,
  Users,
  ChevronLeft,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Minus,
  UserCheck,
  NotebookPen,
  Timer,
  ArrowRight,
  Pencil,
  X,
  Save,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type Training = {
  id: string;
  date: string;
  team_id: string | null;
  notes: string | null;
  location: string | null;
};

type AttendanceRecord = {
  id: string;
  was_present: boolean;
};

type JsonPlayer = {
  id: string;
  firstName?: string;
  lastName?: string;
  number?: string | number;
};

type SelfTarget = {
  key: string;
  jsonId: string;
  label: string;
};

type PlanExercise = {
  id: string;
  kind: string;
  name: string;
  duration?: string;
};

const PLAN_KIND_LABELS: Record<string, string> = {
  echauffement: "Échauffement",
  exercice: "Exercice",
  prepa: "Préparation physique",
  shooting: "Shooting",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function formatFullDate(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toDatetimeLocal(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TeamTrainingDetails() {
  const { clubId, teamId, trainingId } = useParams<{ clubId: string; teamId: string; trainingId: string }>();

  const [training, setTraining] = useState<Training | null>(null);
  const [teamName, setTeamName] = useState("");
  const [allPlayers, setAllPlayers] = useState<{ jsonId: string; name: string }[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Map<string, AttendanceRecord>>(new Map());
  const [selfTargets, setSelfTargets] = useState<SelfTarget[]>([]);
  const [planExercises, setPlanExercises] = useState<PlanExercise[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState<string | null>(null);

  // ── Edit training (date / location) ──
  const [editOpen, setEditOpen] = useState(false);
  const [editDate, setEditDate] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editScope, setEditScope] = useState<"this" | "all">("this");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    if (!trainingId || !clubId || !teamId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Utilisateur non connecté.");

        const [trainingRes, attendanceRes, teamRes, myMemberRes, clubRoleRes, membersRes, planRes] = await Promise.all([
          supabase
            .from("trainings")
            .select("id, date, team_id, notes, location")
            .eq("id", trainingId)
            .eq("team_id", teamId)
            .maybeSingle(),
          supabase
            .from("training_attendance")
            .select("id, player_json_id, player_id, was_present")
            .eq("training_id", trainingId),
          supabase
            .from("teams")
            .select("team_name, players")
            .eq("id", teamId)
            .maybeSingle(),
          supabase
            .from("team_members")
            .select("id, role, player_json_id")
            .eq("team_id", teamId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("club_members")
            .select("role")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("team_members")
            .select("player_json_id, user_id")
            .eq("team_id", teamId)
            .not("player_json_id", "is", null),
          supabase
            .from("training_plans")
            .select("exercises")
            .eq("training_id", trainingId)
            .maybeSingle(),
        ]);

        if (trainingRes.error) throw trainingRes.error;
        if (attendanceRes.error) throw attendanceRes.error;

        const t = trainingRes.data as Training | null;
        if (!t) throw new Error("Entraînement introuvable.");

        const clubRole = clubRoleRes.data?.role ?? null;
        const myRole = myMemberRes.data?.role ?? null;
        const editable =
          ["dirigeant", "cadre_technique"].includes(clubRole ?? "") ||
          ["owner", "coach", "assistant"].includes(myRole ?? "");

        // 1. Build name map from teams.players JSON
        let tName = "Équipe";
        const nameMap = new Map<string, string>();

        if (teamRes.data) {
          if (teamRes.data.team_name) tName = teamRes.data.team_name.trim();

          const jsonPlayers: JsonPlayer[] = Array.isArray(teamRes.data.players)
            ? (teamRes.data.players as JsonPlayer[])
            : [];

          for (const p of jsonPlayers) {
            if (!p.id) continue;
            const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
            nameMap.set(p.id, name || `Joueur #${p.number ?? p.id}`);
          }
        }

        // 2. Overwrite with user_profiles where linked
        if (membersRes.data && membersRes.data.length > 0) {
          const userIds = (membersRes.data as any[]).map((m) => m.user_id).filter(Boolean);
          if (userIds.length > 0) {
            const profilesRes = await supabase
              .from("user_profiles")
              .select("id, full_name, pseudo, public_use_pseudo")
              .in("id", userIds);

            const profileMap = new Map<string, string>();
            for (const prof of profilesRes.data || []) {
              const name =
                prof.public_use_pseudo && prof.pseudo
                  ? prof.pseudo
                  : prof.full_name?.trim() || null;
              if (name) profileMap.set(prof.id, name);
            }

            for (const member of membersRes.data as any[]) {
              if (!member.player_json_id || !member.user_id) continue;
              const profName = profileMap.get(member.user_id);
              if (profName) nameMap.set(member.player_json_id, profName);
            }
          }
        }

        // 3. Build attendance map
        const aMap = new Map<string, AttendanceRecord>();
        for (const row of (attendanceRes.data || []) as any[]) {
          const key = row.player_json_id ?? row.player_id;
          if (!key) continue;
          aMap.set(key, { id: row.id, was_present: row.was_present });
        }

        // 4. Build full player list
        const players = Array.from(nameMap.entries())
          .map(([jsonId, name]) => ({ jsonId, name }))
          .sort((a, b) => a.name.localeCompare(b.name, "fr"));

        // 5. Who am I allowed to self-register? (myself if player, or my linked children if parent)
        const targets: SelfTarget[] = [];
        if (myRole === "player" && myMemberRes.data?.player_json_id) {
          const jsonId = myMemberRes.data.player_json_id as string;
          targets.push({ key: "self", jsonId, label: nameMap.get(jsonId) || "Moi" });
        } else if (myRole === "parent" && myMemberRes.data?.id) {
          const { data: linkRows, error: linkErr } = await supabase
            .from("team_player_parents")
            .select("player_member_id")
            .eq("parent_member_id", myMemberRes.data.id);

          if (!linkErr && linkRows && linkRows.length > 0) {
            const playerMemberIds = linkRows.map((r: any) => r.player_member_id);
            const { data: childRows } = await supabase
              .from("team_members")
              .select("id, player_json_id")
              .in("id", playerMemberIds)
              .not("player_json_id", "is", null);

            for (const child of childRows || []) {
              const jsonId = (child as any).player_json_id as string;
              targets.push({ key: child.id, jsonId, label: nameMap.get(jsonId) || "Mon enfant" });
            }
          }
        }

        const exercises = Array.isArray(planRes.data?.exercises)
          ? (planRes.data!.exercises as PlanExercise[])
          : [];

        if (!cancelled) {
          setTraining(t);
          setTeamName(tName);
          setAllPlayers(players);
          setAttendanceMap(aMap);
          setCanEdit(editable);
          setSelfTargets(targets);
          setPlanExercises(exercises);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger l'entraînement.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [trainingId, clubId, teamId]);

  // ── Toggle attendance (works for staff editing anyone, or self/child sign-up) ──

  async function handleToggle(jsonId: string, newPresent: boolean) {
    if (!trainingId || saving) return;
    setSaving(jsonId);

    const existing = attendanceMap.get(jsonId);

    if (existing) {
      const { error } = await supabase
        .from("training_attendance")
        .update({ was_present: newPresent })
        .eq("id", existing.id);

      if (!error) {
        setAttendanceMap((prev) => {
          const next = new Map(prev);
          next.set(jsonId, { ...existing, was_present: newPresent });
          return next;
        });
      }
    } else {
      const { data, error } = await supabase
        .from("training_attendance")
        .insert({ training_id: trainingId, player_json_id: jsonId, was_present: newPresent })
        .select("id")
        .single();

      if (!error && data) {
        setAttendanceMap((prev) => {
          const next = new Map(prev);
          next.set(jsonId, { id: data.id, was_present: newPresent });
          return next;
        });
      }
    }

    setSaving(null);
  }

  // ── Edit training (date / location) ────────────────────────────────────────

  function openEditModal() {
    if (!training) return;
    setEditDate(toDatetimeLocal(training.date));
    setEditLocation(training.location ?? "");
    setEditScope("this");
    setEditError("");
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!training || !teamId || !editDate) return;
    setEditSaving(true);
    setEditError("");

    const newDateIso = new Date(editDate).toISOString();
    const newLocation = editLocation.trim() || null;

    try {
      const { error: thisErr } = await supabase
        .from("trainings")
        .update({ date: newDateIso, location: newLocation })
        .eq("id", training.id);
      if (thisErr) throw thisErr;

      if (editScope === "all") {
        // Applies the location to every other training of this team (their own dates are untouched)
        const { error: othersErr } = await supabase
          .from("trainings")
          .update({ location: newLocation })
          .eq("team_id", teamId)
          .neq("id", training.id);
        if (othersErr) throw othersErr;
      }

      setTraining({ ...training, date: newDateIso, location: newLocation });
      setEditOpen(false);
    } catch (err: any) {
      setEditError(err?.message || "Erreur lors de la mise à jour de l'entraînement.");
    } finally {
      setEditSaving(false);
    }
  }

  // ── Derived counts ─────────────────────────────────────────────────────────

  const counts = useMemo(() => {
    let present = 0;
    let absent = 0;
    attendanceMap.forEach((r) => { r.was_present ? present++ : absent++; });
    return { present, absent, total: attendanceMap.size };
  }, [attendanceMap]);

  const isUpcoming = training ? new Date(training.date) >= new Date() : false;

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement de l'entraînement…
        </div>
      </div>
    );
  }

  if (error || !training) {
    return (
      <div className="space-y-4">
        <NavLink
          to={`/club/${clubId}/team/${teamId}/events`}
          className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Retour aux événements
        </NavLink>
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error || "Entraînement introuvable."}
        </div>
      </div>
    );
  }

  // Players visible to non-editors: only those with a recorded attendance
  const visiblePlayers = canEdit
    ? allPlayers
    : allPlayers.filter((p) => attendanceMap.has(p.jsonId));

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Back */}
      <NavLink
        to={`/club/${clubId}/team/${teamId}/events`}
        className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 hover:underline"
      >
        <ChevronLeft className="h-4 w-4" />
        Retour aux événements
      </NavLink>

      {/* Header */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-start sm:justify-between sm:text-left">
          <div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Entraînement</div>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
              <Dumbbell className="h-5 w-5 text-gray-500" />
              {teamName}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isUpcoming ? (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300">
                <Clock className="h-3 w-3" />
                À venir
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                Passé
              </span>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={openEditModal}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <Pencil className="h-3.5 w-3.5" />
                Modifier
              </button>
            )}
            {canEdit && isUpcoming && trainingId && (
              <SendReminderButton type="training" eventId={trainingId} />
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <CalendarDays className="h-4 w-4" />
              Date &amp; heure
            </div>
            <div className="mt-2 text-sm text-gray-900 dark:text-gray-100 capitalize">
              {formatFullDate(training.date)}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <MapPin className="h-4 w-4" />
              Lieu
            </div>
            <div className="mt-2 text-sm text-gray-900 dark:text-gray-100">
              {training.location?.trim() || <span className="text-gray-400 dark:text-gray-600">—</span>}
            </div>
          </div>

          {training.notes && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4 sm:col-span-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <FileText className="h-4 w-4" />
                Notes
              </div>
              <div className="mt-2 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {training.notes}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Training plan preview */}
      {planExercises.length > 0 && (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <NotebookPen className="h-4 w-4" />
              Plan d'entraînement
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                ({planExercises.length} bloc{planExercises.length !== 1 ? "s" : ""})
              </span>
            </div>
            <NavLink
              to={`/plandetails?training_id=${trainingId}`}
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200 hover:underline"
            >
              Voir le plan complet
              <ArrowRight className="h-3.5 w-3.5" />
            </NavLink>
          </div>

          <div className="mt-3 space-y-2">
            {planExercises.map((ex, idx) => (
              <div
                key={ex.id || idx}
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-2.5"
              >
                <div className="min-w-0 flex items-center gap-2">
                  <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {PLAN_KIND_LABELS[ex.kind] || "Bloc"}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{ex.name}</span>
                </div>
                {ex.duration && (
                  <span className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
                    <Timer className="h-3 w-3" />
                    {ex.duration}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self sign-up */}
      {selfTargets.length > 0 && (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <UserCheck className="h-4 w-4" />
            Mon inscription
          </div>

          <div className="mt-3 space-y-2">
            {selfTargets.map((target) => {
              const record = attendanceMap.get(target.jsonId);
              const isSaving = saving === target.jsonId;

              return (
                <div
                  key={target.key}
                  className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-2.5"
                >
                  <div className="text-sm text-gray-900 dark:text-gray-100">{target.label}</div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleToggle(target.jsonId, true)}
                          disabled={!!saving}
                          className={cx(
                            "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition",
                            record?.was_present === true
                              ? "bg-green-600 text-white border-green-600"
                              : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-green-400 hover:text-green-700 dark:hover:text-green-400"
                          )}
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Je participe
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggle(target.jsonId, false)}
                          disabled={!!saving}
                          className={cx(
                            "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition",
                            record?.was_present === false
                              ? "bg-red-600 text-white border-red-600"
                              : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-red-400 hover:text-red-700 dark:hover:text-red-400"
                          )}
                        >
                          <XCircle className="h-3 w-3" />
                          Je ne participe pas
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Attendance */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <Users className="h-4 w-4" />
            Présences
            {visiblePlayers.length > 0 && (
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                ({visiblePlayers.length} joueur{visiblePlayers.length !== 1 ? "s" : ""})
              </span>
            )}
          </div>
          {canEdit && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Cliquez sur un joueur pour modifier sa présence
            </span>
          )}
        </div>

        {counts.total > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Présents : {counts.present}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              <XCircle className="h-3.5 w-3.5" />
              Absents : {counts.absent}
            </span>
            {canEdit && allPlayers.length - counts.total > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <Minus className="h-3.5 w-3.5" />
                Non renseignés : {allPlayers.length - counts.total}
              </span>
            )}
          </div>
        )}

        {visiblePlayers.length === 0 ? (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {canEdit
              ? "Aucun joueur trouvé dans cette équipe."
              : isUpcoming
              ? "Les présences seront enregistrées après l'entraînement."
              : "Aucune présence enregistrée pour cet entraînement."}
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {visiblePlayers.map((p) => {
              const record = attendanceMap.get(p.jsonId);
              const isSaving = saving === p.jsonId;

              return (
                <div
                  key={p.jsonId}
                  className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-2.5"
                >
                  <div className="text-sm text-gray-900 dark:text-gray-100">{p.name}</div>

                  {canEdit ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleToggle(p.jsonId, true)}
                            disabled={!!saving}
                            className={cx(
                              "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition",
                              record?.was_present === true
                                ? "bg-green-600 text-white border-green-600"
                                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-green-400 hover:text-green-700 dark:hover:text-green-400"
                            )}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Présent
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(p.jsonId, false)}
                            disabled={!!saving}
                            className={cx(
                              "inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition",
                              record?.was_present === false
                                ? "bg-red-600 text-white border-red-600"
                                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-red-400 hover:text-red-700 dark:hover:text-red-400"
                            )}
                          >
                            <XCircle className="h-3 w-3" />
                            Absent
                          </button>
                        </>
                      )}
                    </div>
                  ) : record ? (
                    record.was_present ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        <CheckCircle2 className="h-3 w-3" />
                        Présent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        <XCircle className="h-3 w-3" />
                        Absent
                      </span>
                    )
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit training modal */}
      {editOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-lg rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Modifier l'entraînement</div>
                <button type="button" onClick={() => setEditOpen(false)}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  <X className="h-4 w-4" />Fermer
                </button>
              </div>

              {editError && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {editError}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Date &amp; heure</label>
                  <input
                    type="datetime-local"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Lieu</label>
                  <input
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    placeholder="Gymnase / Salle / Extérieur…"
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Appliquer à</label>
                  <div className="mt-1 space-y-2">
                    <label className="flex items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editScope"
                        checked={editScope === "this"}
                        onChange={() => setEditScope("this")}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        Seulement cet entraînement
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          La date et le lieu ne changent que pour cette séance.
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2 cursor-pointer">
                      <input
                        type="radio"
                        name="editScope"
                        checked={editScope === "all"}
                        onChange={() => setEditScope("all")}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        Toutes les séances de l'équipe
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          Le lieu sera appliqué à tous les entraînements de l'équipe. La date ne concerne que cette séance.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4">
                <button type="button" onClick={() => setEditOpen(false)}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={editSaving || !editDate}
                  className={cx(
                    "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                    "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                    (editSaving || !editDate) && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editSaving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
